import { z } from "zod";
import {
  SocialFindingSeveritySchema,
  SocialJudgmentSchema,
  SocialPlatformSchema,
  SocialProfileStatusSchema,
  SocialStandardSchema,
} from "../../enums/social";
import {
  SocialCaptureResponseSchema,
  SocialExtractionResponseSchema,
} from "./admin";

const rollupCount = z.number().int().min(0);

/**
 * Latest-attempt verdict counts for one standard across every analysed post of
 * a profile. The four verdicts stay separate on the wire: `unableToVerify` and
 * `notApplicable` record that the evidence could not settle the question and
 * must never be folded into `fail` by a consumer.
 */
export const SocialReportStandardRollupSchema = z.object({
  standard: SocialStandardSchema,
  verdicts: z.record(SocialJudgmentSchema, rollupCount),
});
export type SocialReportStandardRollup = z.infer<
  typeof SocialReportStandardRollupSchema
>;

export const SocialReportProfileRollupSchema = z.object({
  capturedPosts: rollupCount,
  analysedPosts: rollupCount,
  failedAnalyses: rollupCount,
  standards: z.array(SocialReportStandardRollupSchema),
  findings: z.record(SocialFindingSeveritySchema, rollupCount),
});
export type SocialReportProfileRollup = z.infer<
  typeof SocialReportProfileRollupSchema
>;

export const SocialReportProfileSchema = z.object({
  id: z.uuid(),
  platform: SocialPlatformSchema,
  profileUrl: z.url(),
  handle: z.string(),
  displayName: z.string().nullable(),
  avatarUrl: z.url().nullable(),
  status: SocialProfileStatusSchema,
  latestExtraction: SocialExtractionResponseSchema.nullable(),
  rollup: SocialReportProfileRollupSchema,
});
export type SocialReportProfile = z.infer<typeof SocialReportProfileSchema>;

/**
 * The client-facing social report: registered profiles with their latest
 * extraction state and whole-history rollups, plus a bounded window of the
 * newest captured posts carrying their latest analysis. Rollups aggregate in
 * SQL over all history; `posts` alone is windowed, so the two can differ on
 * clients with more captures than the read limit.
 */
export const SocialReportResponseSchema = z.object({
  clientId: z.uuid(),
  profiles: z.array(SocialReportProfileSchema),
  posts: z.array(SocialCaptureResponseSchema),
});
export type SocialReportResponse = z.infer<typeof SocialReportResponseSchema>;
